import { Component, OnInit, ChangeDetectorRef, HostListener } from '@angular/core';
import { CustomerProgressService } from '../../../service/customer-progress.service';
import { TranslateService } from '@ngx-translate/core';
import { MatDialogRef } from '@angular/material/dialog';
import { Router } from '@angular/router';
import { trigger, style, transition, animate } from '@angular/animations';

@Component({
  selector: 'app-customer-progress-dialog',
  templateUrl: './customer-progress-dialog.component.html',
  styleUrls: ['./customer-progress-dialog.component.scss'],
  animations: [
    trigger('fadeInUp', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(20px)' }),
        animate('300ms ease-out', style({ opacity: 1, transform: 'translateY(0)' }))
      ])
    ]),
    trigger('fadeInLeft', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(-20px)' }),
        animate('300ms 100ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ]),
    trigger('fadeInRight', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateX(20px)' }),
        animate('300ms 200ms ease-out', style({ opacity: 1, transform: 'translateX(0)' }))
      ])
    ])
  ]
})
export class CustomerProgressDialogComponent implements OnInit {
  panelOpenState = false;
  fullPercentage: number;
  BRANCHCONFIG = JSON.parse(window.localStorage.getItem('BRANCH_CONFIG'));
  branchId = JSON.parse(window.localStorage.getItem('BRANCH_CONFIG')).id;

  // Setup groups mapped by backend progress code. The backend only returns the
  // codes that belong to the customer's subscribed package/modules, so groups
  // and tasks are built dynamically from the codes actually returned.
  private readonly moduleDefinitions = [
    {
      name: 'BRANCHES',
      stages: [
        { code: 'isBasic', link: '/pages/branch/basic' },
        { code: 'isDetail', link: '/pages/branch/details' },
        { code: 'isBusiness', link: '/pages/branch/business' },
        { code: 'isAddress', link: '/pages/branch/address' },
        { code: 'isDeliveryMethods', link: '/pages/branch/delivery-method' },
        { code: 'isWorkingDays', link: '/pages/branch/working-days' },
      ],
    },
    {
      name: 'INVENTORY',
      stages: [
        { code: 'isSupplier', link: '/pages/shopping-customer/suppliers' },
        { code: 'isCategory', link: '/pages/shopping-customer/customer-categories' },
        { code: 'isSubCategory', link: '/pages/shopping-customer/customer-categories' },
        { code: 'isItem', link: '/pages/shopping-customer/customer-items' },
        { code: 'isBrand', link: '/pages/shopping-customer/customer-brands' },
      ],
    },
    {
      name: 'HR',
      stages: [
        { code: 'isEmployee', link: '/pages/hr/customer-employees' },
        { code: 'isDepartment', link: '/pages/hr/departments' },
        { code: 'isJobs', link: '/pages/hr/jobs' },
        { code: 'isPosition', link: '/pages/hr/positions' },
        { code: 'isCalendarException', link: '/pages/shopping-customer/calendar-exception' },
      ],
    },
    {
      name: 'ACCOUNTING',
      stages: [{ code: 'isAccount', link: '/pages/finance/finance-setting' }],
    },
  ];

  modulesOfProgress: any[] = [];
  // Responsive detection properties
  private readonly mobileBreakpoint = 576;
  private readonly tabletBreakpoint = 992;

  // Reactive properties for viewport detection
  public isMobile = false;
  public isTablet = false;
  public isDesktop = false;

  constructor(
    private _customerProgressService: CustomerProgressService,
    public _translate: TranslateService,
    public ItemDialogRef: MatDialogRef<CustomerProgressDialogComponent>,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {
    this.loadProgress();
  }

  /**
   * Load persisted progress and build setup groups dynamically from the codes
   * returned by the backend (already filtered by the customer's package/modules).
   */
  private loadProgress(): void {
    this._customerProgressService
      .getCustomerProgress(this.BRANCHCONFIG.id)
      .subscribe((res) => {
        const stages = res?.data || [];
        const byCode = new Map<string, any>();
        stages.forEach((s) => byCode.set(s.code, s));

        this.modulesOfProgress = this.moduleDefinitions
          .map((def) => {
            const applicable = def.stages.filter((st) => byCode.has(st.code));
            return {
              name: def.name,
              progressStages: applicable.map((st) => ({
                code: st.code,
                flag: !!byCode.get(st.code).flag,
              })),
              stagesLinks: applicable.map((st) => st.link),
              progressPercentage: null,
            };
          })
          .filter((m) => m.progressStages.length > 0);

        this.modulesOfProgress.forEach((_, i) => this.getPercentage(i));
        this.calculateFullPercentage();
        this.cdr.detectChanges();
      });
  }

  /**
   * Overall configuration progress from applicable module percentages only.
   */
  private calculateFullPercentage(): void {
    if (!this.modulesOfProgress.length) {
      this.fullPercentage = 0;
      return;
    }
    const sum = this.modulesOfProgress.reduce(
      (acc, module) => acc + (module.progressPercentage || 0),
      0
    );
    this.fullPercentage = Math.round(sum / this.modulesOfProgress.length);
  }
  ngOnInit(): void {
    // Initialize viewport detection
    this.updateViewportDetection();
    console.log('CustomerProgressDialog initialized', {
      isMobile: this.isMobile,
      isTablet: this.isTablet,
      isDesktop: this.isDesktop,
      windowWidth: window.innerWidth
    });
  }



  /**
   * Listen for window resize events to update responsive behavior
   */
  @HostListener('window:resize', ['$event'])
  onResize(event: Event): void {
    console.log(event)
    this.updateViewportDetection();
  }

  /**
   * Update viewport detection based on current window size
   */
  private updateViewportDetection(): void {
    const windowWidth = window.innerWidth;

    this.isMobile = windowWidth < this.mobileBreakpoint;
    this.isTablet = windowWidth >= this.mobileBreakpoint && windowWidth < this.tabletBreakpoint;
    this.isDesktop = windowWidth >= this.tabletBreakpoint;

    // Force change detection for reactive updates
    this.cdr.detectChanges();

    // Debug logging
    console.log('Viewport updated:', {
      windowWidth,
      isMobile: this.isMobile,
      isTablet: this.isTablet,
      isDesktop: this.isDesktop
    });
  }

  getPercentage(index) {
    this.modulesOfProgress[index].progressPercentage = Math.round(
      (this.modulesOfProgress[index].progressStages
        .map((stage) => {
          return stage.flag;
        })
        .filter((flag) => flag === true).length /
        this.modulesOfProgress[index].progressStages.map((stage) => {
          return stage.flag;
        }).length) *
      100
    );
  }

  closeDialog(route, name) {
    if (name === 'BRANCHES') {
      sessionStorage.setItem('branchStepsId', JSON.stringify(this.branchId));
      // const navigationExtras: NavigationExtras = {
      //   queryParams: {
      //     id: this.branchId
      //   },
      //   skipLocationChange: true,
      // };
      this.router.navigate([route]);
    }
    this.ItemDialogRef.close();
  }

  endTask(event, stage, moduleIndex: number) {
    event.stopPropagation();
    this.setStageDone(stage, moduleIndex);
  }

  /**
   * Get icon for each module
   */
  getModuleIcon(moduleName: string): string {
    const iconMap = {
      'BRANCHES': 'store',
      'INVENTORY': 'inventory_2',
      'HR': 'people',
      'ACCOUNTING': 'account_balance'
    };
    return iconMap[moduleName] || 'settings';
  }

  /**
   * Get progress text based on percentage
   */
  getProgressText(percentage: number): string {
    if (percentage === 100) {
      return this._translate.instant('PROGRESS.COMPLETED');
    } else if (percentage > 0) {
      return this._translate.instant('PROGRESS.IN_PROGRESS');
    } else {
      return this._translate.instant('PROGRESS.NOT_STARTED');
    }
  }

  /**
   * Get status text based on percentage
   */
  getStatusText(percentage: number): string {
    if (percentage === 100) {
      return this._translate.instant('PROGRESS.ALL_TASKS_COMPLETE');
    } else if (percentage > 0) {
      return this._translate.instant('PROGRESS.SOME_TASKS_COMPLETE');
    } else {
      return this._translate.instant('PROGRESS.NO_TASKS_COMPLETE');
    }
  }

  /**
   * Get progress bar color based on percentage
   */
  getProgressColor(percentage: number): string {
    if (percentage === 100) {
      return 'accent';
    } else if (percentage > 50) {
      return 'primary';
    } else if (percentage > 0) {
      return 'warn';
    } else {
      return '';
    }
  }

  /**
   * Get count of completed tasks for a module
   */
  getCompletedTasks(module): number {
    return module.progressStages.filter((stage) => stage.flag === true).length;
  }

  /**
   * Navigate to task with enhanced tracking
   */
  navigateToTask(route: string, moduleName: string, event: Event): void {
    event.stopPropagation();
    this.closeDialog(route, moduleName);
  }

  /**
   * Mark task as complete with enhanced feedback
   */
  markTaskComplete(event: Event, stage, moduleIndex: number): void {
    event.stopPropagation();
    event.preventDefault();
    this.setStageDone(stage, moduleIndex);
  }

  /**
   * Optimistically mark a stage done, persist it, and revert on failure so the
   * completed state is never shown permanently unless persistence succeeds.
   */
  private setStageDone(stage, moduleIndex: number): void {
    if (stage.flag) {
      return;
    }
    stage.flag = true;
    this.getPercentage(moduleIndex);
    this.calculateFullPercentage();

    this._customerProgressService
      .updateProgressStatuses(this.buildStatusPayload())
      .subscribe({
        error: () => {
          stage.flag = false;
          this.getPercentage(moduleIndex);
          this.calculateFullPercentage();
          this.cdr.detectChanges();
        },
      });
  }

  /**
   * Build the persistence payload from the current state of applicable stages.
   * Completed stages are sent as 1; the backend recomputes the rest (0).
   */
  private buildStatusPayload() {
    const payload: any = { customerBranchId: this.branchId };
    this.modulesOfProgress.forEach((module) =>
      module.progressStages.forEach((stage) => {
        payload[stage.code] = stage.flag ? 1 : 0;
      })
    );
    return payload;
  }
  /**
   * Detects if the current viewport is mobile size (reactive)
   */
  isMobileView(): boolean {
    return this.isMobile;
  }

  /**
   * Detects if the current viewport is tablet size (reactive)
   */
  isTabletView(): boolean {
    return this.isTablet;
  }

  /**
   * Detects if the current viewport is desktop size (reactive)
   */
  isDesktopView(): boolean {
    return this.isDesktop;
  }
  /**
   * Continue setup by navigating to the first incomplete stage
   */
  continueSetup(module, event: Event): void {
    event.stopPropagation();

    try {
      // Find the first incomplete stage (where flag is false)
      const firstIncompleteStageIndex = module.progressStages.findIndex((stage) => !stage.flag);

      if (firstIncompleteStageIndex === -1) {
        console.log('All stages completed for module:', module.name);
        return;
      }

      if (!module.stagesLinks || module.stagesLinks.length <= firstIncompleteStageIndex) {
        console.warn('No matching route found for incomplete stage in module:', module.name);
        return;
      }

      const routeToNavigate = module.stagesLinks[firstIncompleteStageIndex];
      const incompleteStage = module.progressStages[firstIncompleteStageIndex];

      console.log('Continuing setup:', {
        module: module.name,
        stage: incompleteStage.code,
        route: routeToNavigate,
        stageIndex: firstIncompleteStageIndex
      });

      // Handle special navigation for BRANCHES module
      if (module.name === 'BRANCHES') {
        sessionStorage.setItem('branchStepsId', JSON.stringify(this.branchId));
        this.router.navigate([routeToNavigate]);
      } else {
        this.router.navigate([routeToNavigate]);
      }

      // Close the dialog
      this.ItemDialogRef.close();
    } catch (error) {
      console.error('Error in continueSetup:', error);
    }
  }
  /**
   * Check if a module has incomplete stages
   */
  hasIncompleteStages(module): boolean {
    return module.progressStages.some((stage) => !stage.flag);
  }

  /**
   * Get the next incomplete stage for a module
   */
  getNextIncompleteStage(module) {
    return module.progressStages.find((stage) => !stage.flag);
  }

  /**
   * Get continue button text based on module state
   */
  getContinueButtonText(module): string {
    const nextStage = this.getNextIncompleteStage(module);
    if (nextStage) {
      const stageTranslation = this._translate.instant('PROGRESS.' + nextStage.code);
      if (stageTranslation && stageTranslation !== 'PROGRESS.' + nextStage.code) {
        return this._translate.instant('PROGRESS.CONTINUE_TO', { stage: stageTranslation });
      }
    }
    return this._translate.instant('PROGRESS.CONTINUE_SETUP');
  }

  /**
   * Get count of completed modules
   */
  getCompletedModules(): number {
    return this.modulesOfProgress.filter(module => module.progressPercentage === 100).length;
  }
}
